import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Building2, Search, Plus, Trash2, Edit, MapPin, SearchX, Image as ImageIcon, Loader2, Upload } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function HotelsManager() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('All');
  
  // Selection and Pagination
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    location: '',
    category: '3-star',
    images: []
  });

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const res = await api.getHotels();
      setHotels(res.items || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [search, locationFilter]);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} hotel(s)?`)) return;
    setIsSubmitting(true);
    try {
      await Promise.all(selectedIds.map(id => api.deleteHotel(id)));
      toast.success(`${selectedIds.length} hotel(s) deleted`);
      setHotels(prev => prev.filter(h => !selectedIds.includes(h.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete some hotels');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) return;
    try {
      await api.deleteHotel(id);
      toast.success('Hotel deleted');
      setHotels(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete hotel');
    }
  };

  const handleEdit = (hotel) => {
    setFormData({
      id: hotel.id,
      name: hotel.name || '',
      location: hotel.location || '',
      category: hotel.category || '3-star',
      images: hotel.images || []
    });
    setIsModalOpen(true);
  };

  const openNew = () => {
    setFormData({ id: null, name: '', location: '', category: '3-star', images: [] });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.location.trim()) {
      toast.error('Name and location are required');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.saveHotel(formData);
      toast.success(formData.id ? 'Hotel updated' : 'Hotel added');
      setIsModalOpen(false);
      fetchHotels();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save hotel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ url: ev.target.result, name: file.name });
        reader.readAsDataURL(file);
      });
    });
    
    const newImages = await Promise.all(promises);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages].slice(0, 3) // Max 3
    }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const uniqueLocations = [...new Set(hotels.map(h => h.location).filter(Boolean))].sort();

  const filtered = hotels
    .filter(h => 
      (h.name || '').toLowerCase().includes(search.toLowerCase()) || 
      (h.location || '').toLowerCase().includes(search.toLowerCase())
    )
    .filter(h => locationFilter === 'All' || h.location === locationFilter)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedItems.length && paginatedItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedItems.map(h => h.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(x => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#D4AF37]" /> Hotel Manager
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage your master hotel database used for auto-suggestions</p>
        </div>
        <Button onClick={openNew} className="btn-luxe  text-white rounded-xl shadow-lg transition-all font-semibold px-5">
          <Plus className="w-4 h-4 mr-2" /> Add Hotel
        </Button>
      </div>

      <Card className="glass-panel-dark border-slate-200 rounded-3xl overflow-hidden p-6">
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input 
              placeholder="Search by hotel name or location..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 w-full"
            />
          </div>
          <div className="w-full md:w-64 shrink-0">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200">
                <SelectValue placeholder="Filter by Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Locations</SelectItem>
                {uniqueLocations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="btn-luxe/20 border border-[#1D4ED8]/30 rounded-xl p-3 mb-6 flex items-center justify-between">
            <span className="text-sm text-blue-200 font-medium ml-2">{selectedIds.length} hotel(s) selected</span>
            <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={isSubmitting} className="rounded-lg">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete Selected
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col space-y-4 py-10">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 bg-slate-50/50 rounded-xl animate-pulse">
                <div className="w-5 h-5 bg-slate-200 rounded shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                  <div className="h-3 bg-slate-100 rounded w-1/4"></div>
                </div>
                <div className="w-16 h-6 bg-slate-200 rounded shrink-0"></div>
                <div className="w-12 h-4 bg-slate-200 rounded shrink-0"></div>
                <div className="flex gap-2 shrink-0">
                  <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                  <div className="w-8 h-8 bg-slate-200 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <SearchX className="w-12 h-12 mb-3 text-slate-500" />
            <p>No hotels found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-[#ea580c] uppercase bg-orange-50 border-b-2 border-orange-200">
                <tr>
                  <th className="px-5 py-4 font-bold tracking-wider rounded-tl-xl w-12">
                    <Checkbox 
                      checked={paginatedItems.length > 0 && selectedIds.length === paginatedItems.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-orange-400 data-[state=checked]:bg-[#ea580c]"
                    />
                  </th>
                  <th className="px-5 py-4 font-bold tracking-wider">Hotel Name</th>
                  <th className="px-5 py-4 font-bold tracking-wider">Location</th>
                  <th className="px-5 py-4 font-bold tracking-wider">Category</th>
                  <th className="px-5 py-4 font-bold tracking-wider">Photos</th>
                  <th className="px-5 py-4 font-bold tracking-wider text-right rounded-tr-xl">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((hotel) => (
                  <tr key={hotel.id} className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <Checkbox 
                        checked={selectedIds.includes(hotel.id)}
                        onCheckedChange={() => toggleSelect(hotel.id)}
                        className="data-[state=checked]:bg-[#ea580c] border-slate-300"
                      />
                    </td>
                    <td className="px-5 py-4 font-bold text-slate-800">{hotel.name}</td>
                    <td className="px-5 py-4 text-slate-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#ea580c]" /> {hotel.location}</td>
                    <td className="px-5 py-4">
                      <span className="px-3 py-1.5 bg-orange-100 text-[#ea580c] rounded-lg text-xs font-bold border border-orange-200">{hotel.category || 'N/A'}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <ImageIcon className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-semibold text-slate-600">{(hotel.images || []).length}/3</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(hotel)} className="h-8 w-8 text-[#ea580c] hover:bg-orange-100">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(hotel.id)} className="h-8 w-8 text-red-500 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 px-2">
                <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-slate-200 text-slate-800 hover:bg-slate-100">Previous</Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-slate-200 text-slate-800 hover:bg-slate-100">Next</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-white/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="glass-panel-dark text-slate-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border-slate-200">
            <div className="bg-slate-50 p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-slate-800">
                {formData.id ? 'Edit Hotel' : 'Add New Hotel'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-slate-800">✕</button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-slate-800">Hotel Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    placeholder="e.g., The Himalayan Resort"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-800">Location (City)</Label>
                  <Input 
                    value={formData.location} 
                    onChange={(e) => setFormData({...formData, location: e.target.value})} 
                    placeholder="e.g., Manali"
                    className="mt-1 bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-800">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="mt-1 bg-slate-50 border-slate-200 text-slate-800 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-800">
                      <SelectItem value="Budget">Budget</SelectItem>
                      <SelectItem value="3-star">3-star</SelectItem>
                      <SelectItem value="4-star">4-star</SelectItem>
                      <SelectItem value="5-star">5-star</SelectItem>
                      <SelectItem value="Luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-2">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-slate-800">Photos ({formData.images.length}/3)</Label>
                  <label htmlFor="hotel-img-upload">
                    <Button variant="outline" size="sm" className="h-8 text-xs border-slate-200 text-slate-800 hover:bg-slate-100 hover:text-white" asChild>
                      <span><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</span>
                    </Button>
                  </label>
                  <input id="hotel-img-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                </div>
                
                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {formData.images.map((img, i) => (
                      <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-video">
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removeImage(i)} className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-dashed border-white/20 rounded-xl p-6 text-center text-sm text-slate-500">
                    No photos uploaded yet.
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" className="border-slate-200 text-slate-800 hover:bg-slate-100 hover:text-white" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={isSubmitting} className="btn-luxe  text-white">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Hotel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
